/**
 * C Sidecart JavaScript
 * Handles drawer open/close, cart API operations, Swiper init, and progress bar updates
 * All cart operations are asynchronous - no page refresh required
 */

class CSidecart {
  constructor() {
    this.sidecart = document.querySelector('[data-sidecart]');
    this.overlay = document.querySelector('[data-sidecart-overlay]');
    this.closeButtons = document.querySelectorAll('[data-sidecart-close]');
    this.openButtons = document.querySelectorAll('[data-sidecart-open]');
    this.swiperContainer = document.querySelector('[data-sidecart-addons-swiper]');

    this.swiper = null;
    this.isLoading = false;
    // Get section ID from DOM (for section groups) or fallback to default
    this.sectionId = this.sidecart?.dataset?.sectionId || 'c-sidecart';
    this.isInternalRequest = false; // Flag to prevent double-triggering from interceptor
    
    // Rate limiting settings
    this.maxRetries = 3;
    this.baseDelay = 1000; // 1 second base delay for retries

    this.init();
  }

  init() {
    this.bindEvents();
    this.initSwiper();
    this.interceptProductForms();
    this.interceptFetchRequests();
    
    // Log section ID for debugging
    console.log('Sidecart initialized with section ID:', this.sectionId);
  }

  /**
   * Helper method to perform fetch with retry logic for rate limiting
   * @param {string} url - The URL to fetch
   * @param {object} options - Fetch options
   * @param {number} retryCount - Current retry attempt (internal use)
   * @returns {Promise<Response>}
   */
  async fetchWithRetry(url, options, retryCount = 0) {
    try {
      const response = await fetch(url, options);
      
      // Handle rate limiting (429) with exponential backoff
      if (response.status === 429 && retryCount < this.maxRetries) {
        const delay = this.baseDelay * Math.pow(2, retryCount);
        console.warn(`Rate limited (429). Retrying in ${delay}ms... (attempt ${retryCount + 1}/${this.maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchWithRetry(url, options, retryCount + 1);
      }
      
      return response;
    } catch (error) {
      // Retry on network errors as well
      if (retryCount < this.maxRetries) {
        const delay = this.baseDelay * Math.pow(2, retryCount);
        console.warn(`Network error. Retrying in ${delay}ms... (attempt ${retryCount + 1}/${this.maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchWithRetry(url, options, retryCount + 1);
      }
      
      throw error;
    }
  }

  bindEvents() {
    // Open buttons (e.g., cart icon in header)
    this.openButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close buttons
    this.closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        // Only prevent default if it's not a link with a valid href
        const isLinkWithHref = button.tagName === 'A' && button.hasAttribute('href') && button.getAttribute('href') !== '#';
        if (!isLinkWithHref) {
          e.preventDefault();
        }
        this.close();
        // If it's a link, let the browser handle navigation naturally
      });
    });

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Quantity buttons
    document.addEventListener('click', (e) => {
      const increaseBtn = e.target.closest('[data-sidecart-increase]');
      const decreaseBtn = e.target.closest('[data-sidecart-decrease]');
      const removeBtn = e.target.closest('[data-sidecart-remove]');
      const addToCartBtn = e.target.closest('[data-addon-add-to-cart]');

      if (increaseBtn) {
        e.preventDefault();
        const key = increaseBtn.dataset.lineItemKey;
        this.updateQuantity(key, 1);
      }

      if (decreaseBtn) {
        e.preventDefault();
        const key = decreaseBtn.dataset.lineItemKey;
        this.updateQuantity(key, -1);
      }

      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.lineItemKey;
        this.removeItem(key);
      }

      if (addToCartBtn) {
        e.preventDefault();
        e.stopPropagation();
        
        // Get variant ID - try multiple methods
        let variantId = addToCartBtn.dataset.variantId;
        if (!variantId) {
          variantId = addToCartBtn.getAttribute('data-variant-id');
        }
        
        // Also check if button is disabled
        if (addToCartBtn.disabled || addToCartBtn.getAttribute('aria-disabled') === 'true') {
          console.warn('Add to cart button is disabled');
          return;
        }
        
        if (variantId) {
          this.addToCart(variantId);
        } else {
          console.error('No variant ID found on add-on button:', addToCartBtn);
          this.showError('Unable to add item. Please refresh and try again.');
        }
      }

      // Upgrade to subscription button
      const upgradeBtn = e.target.closest('[data-sidecart-upgrade-subscription]');
      if (upgradeBtn) {
        e.preventDefault();
        e.stopPropagation();
        
        const lineItemKey = upgradeBtn.dataset.lineItemKey;
        const variantId = upgradeBtn.dataset.variantId;
        const sellingPlanId = upgradeBtn.dataset.sellingPlanId;
        const quantity = parseInt(upgradeBtn.dataset.quantity, 10) || 1;
        
        if (lineItemKey && variantId && sellingPlanId) {
          this.upgradeToSubscription(lineItemKey, variantId, sellingPlanId, quantity);
        } else {
          console.error('Missing data for subscription upgrade:', { lineItemKey, variantId, sellingPlanId });
          this.showError('Unable to upgrade to subscription. Please try again.');
        }
      }
    });

    // Variant select change for add-ons
    document.addEventListener('change', (e) => {
      const variantSelect = e.target.closest('[data-addon-variant-select]');
      if (variantSelect) {
        const card = variantSelect.closest('[data-addon-card]');
        const addBtn = card.querySelector('[data-addon-add-to-cart]');
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        
        if (addBtn) {
          addBtn.dataset.variantId = variantSelect.value;
          addBtn.disabled = selectedOption.disabled;
          addBtn.textContent = selectedOption.disabled ? 'SOLD OUT' : 'ADD TO CART';
        }
      }
    });

    // Listen for cart updates from other parts of the site
    document.addEventListener('cart:updated', () => {
      this.refreshCart();
    });

    // Listen for custom sidecart:open event
    document.addEventListener('sidecart:open', () => {
      this.open();
    });

    // Listen for custom sidecart:refresh event
    document.addEventListener('sidecart:refresh', () => {
      this.refreshCart();
    });
  }

  /**
   * Intercepts all product forms to handle add-to-cart asynchronously
   * and open the sidecart instead of showing the default notification
   */
  interceptProductForms() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      // Check if this form should use the sidecart
      // Also check parent elements (e.g. product-form custom element with data-no-sidecart)
      const useSidecart = !form.hasAttribute('data-no-sidecart') && !form.closest('[data-no-sidecart]');
      if (!useSidecart) return;

      e.preventDefault();
      e.stopPropagation();

      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton && submitButton.classList.contains('loading')) return;

      // Show loading state on button
      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
        const spinner = form.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      try {
        const formData = new FormData(form);
        const data = {};
        
        // Convert FormData to JSON
        for (let [key, value] of formData.entries()) {
          // Handle selling_plan and other numeric fields
          if (key === 'id' || key === 'quantity' || key === 'selling_plan') {
            data[key] = parseInt(value, 10) || value;
          } else {
            data[key] = value;
          }
        }

        // Ensure quantity is at least 1
        if (!data.quantity) data.quantity = 1;

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(data)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        // Refresh cart and open sidecart
        await this.refreshCart();
        this.dispatchCartUpdate();
        this.open();

      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError(error.message || 'Failed to add item. Please try again.');
      } finally {
        // Reset button state
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          const spinner = form.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.add('hidden');
        }
      }
    }, true);
  }

  /**
   * Intercepts native fetch requests to /cart/add.js to ensure
   * the sidecart is refreshed even when other scripts add products
   */
  interceptFetchRequests() {
    const originalFetch = window.fetch;
    const sidecart = this;

    window.fetch = async function(...args) {
      const response = await originalFetch.apply(this, args);
      
      // Skip if this is an internal sidecart request
      if (sidecart.isInternalRequest) {
        return response;
      }
      
      const url = args[0];
      const urlString = typeof url === 'string' ? url : url.url;
      
      // Check if this is a cart modification request (not our own section refresh)
      if (urlString && !urlString.includes('sections=') && (
        urlString.includes('/cart/add') ||
        urlString.includes('/cart/change') ||
        urlString.includes('/cart/update') ||
        urlString.includes('/cart/clear')
      )) {
        // Clone the response so we can read it
        const clonedResponse = response.clone();
        
        // Only refresh if the request was successful
        if (clonedResponse.ok) {
          // Small delay to ensure cart is updated on server
          setTimeout(() => {
            sidecart.refreshCart();
            sidecart.dispatchCartUpdate();
            
            // Open sidecart only for add requests
            if (urlString.includes('/cart/add')) {
              sidecart.open();
            }
          }, 100);
        }
      }
      
      return response;
    };
  }

  initSwiper() {
    if (!this.swiperContainer || typeof Swiper === 'undefined') {
      console.warn('Swiper not initialized: container or Swiper library not found');
      return;
    }

    try {
      // Destroy existing swiper instance if any
      if (this.swiper) {
        try {
          this.swiper.destroy(true, true);
        } catch (e) {
          // Ignore destroy errors
        }
        this.swiper = null;
      }

      this.swiper = new Swiper(this.swiperContainer, {
        slidesPerView: 'auto',
        spaceBetween: 12,
        freeMode: true,
        breakpoints: {
          768: {
            spaceBetween: 12,
          }
        },
        on: {
          init: function() {
            console.log('Swiper initialized successfully');
          }
        }
      });
    } catch (error) {
      console.error('Error initializing swiper:', error);
    }
  }

  open() {
    if (!this.sidecart) return;

    this.sidecart.classList.add('is-active');
    this.sidecart.setAttribute('aria-hidden', 'false');
    
    if (this.overlay) {
      this.overlay.classList.add('is-active');
    }
    
    document.body.classList.add('sidecart-open');

    // Focus trap - focus on close button
    const closeBtn = this.sidecart.querySelector('[data-sidecart-close]');
    if (closeBtn) {
      setTimeout(() => closeBtn.focus(), 100);
    }
  }

  close() {
    if (!this.sidecart) return;

    this.sidecart.classList.remove('is-active');
    this.sidecart.setAttribute('aria-hidden', 'true');
    
    if (this.overlay) {
      this.overlay.classList.remove('is-active');
    }
    
    document.body.classList.remove('sidecart-open');
  }

  isOpen() {
    return this.sidecart && this.sidecart.classList.contains('is-active');
  }

  async updateQuantity(key, change) {
    if (this.isLoading) return;

    const itemElement = document.querySelector(`[data-line-item-key="${key}"]`);
    const qtyValueElement = itemElement?.querySelector('[data-sidecart-qty-value]');
    
    if (!qtyValueElement) return;

    const currentQty = parseInt(qtyValueElement.textContent, 10);
    const newQty = Math.max(0, currentQty + change);

    if (newQty === 0) {
      return this.removeItem(key);
    }

    this.setLoading(true);
    this.isInternalRequest = true;

    try {
      const response = await this.fetchWithRetry('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: newQty
        })
      });

      // fetchWithRetry already handles 429 retries
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Failed to update cart');
      }

      await this.refreshCart();
      this.dispatchCartUpdate();
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showError(error.message || 'Failed to update quantity. Please try again.');
    } finally {
      this.setLoading(false);
      this.isInternalRequest = false;
    }
  }

  async removeItem(key) {
    if (this.isLoading) return;

    this.setLoading(true);
    this.isInternalRequest = true;

    try {
      const response = await this.fetchWithRetry('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      // fetchWithRetry already handles 429 retries
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Failed to remove item');
      }

      await this.refreshCart();
      this.dispatchCartUpdate();
    } catch (error) {
      console.error('Error removing item:', error);
      this.showError(error.message || 'Failed to remove item. Please try again.');
    } finally {
      this.setLoading(false);
      this.isInternalRequest = false;
    }
  }

  /**
   * Upgrades a single purchase item to a subscription by removing it
   * and re-adding it with a selling plan
   * @param {string} lineItemKey - The key of the line item to upgrade
   * @param {string} variantId - The variant ID
   * @param {string} sellingPlanId - The selling plan ID to apply
   * @param {number} quantity - The quantity to add
   */
  async upgradeToSubscription(lineItemKey, variantId, sellingPlanId, quantity = 1) {
    if (this.isLoading) return;

    const parsedVariantId = parseInt(variantId, 10);
    const parsedSellingPlanId = parseInt(sellingPlanId, 10);

    if (isNaN(parsedVariantId) || isNaN(parsedSellingPlanId)) {
      console.error('Invalid IDs for subscription upgrade:', { variantId, sellingPlanId });
      this.showError('Unable to upgrade to subscription. Invalid product data.');
      return;
    }

    this.setLoading(true);
    this.isInternalRequest = true;

    try {
      // Step 1: Remove the existing line item (single purchase)
      const removeResponse = await this.fetchWithRetry('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: 0
        })
      });

      // fetchWithRetry already handles 429 retries, so just check ok status
      if (!removeResponse.ok) {
        const removeData = await removeResponse.json().catch(() => ({}));
        throw new Error(removeData.description || removeData.message || 'Failed to remove original item');
      }

      // Step 2: Add the item back with the selling plan
      const addResponse = await this.fetchWithRetry('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parsedVariantId,
          quantity: quantity,
          selling_plan: parsedSellingPlanId
        })
      });

      // fetchWithRetry already handles 429 retries
      const contentType = addResponse.headers.get('content-type') || '';
      const isValidContentType = contentType.includes('application/json') || contentType.includes('text/javascript');

      if (!isValidContentType) {
        throw new Error('Unable to upgrade right now. Please refresh the page and try again.');
      }

      const data = await addResponse.json();

      if (!addResponse.ok) {
        throw new Error(data.description || data.message || 'Failed to add subscription item');
      }

      await this.refreshCart();
      this.dispatchCartUpdate();
    } catch (error) {
      console.error('Error upgrading to subscription:', error);
      this.showError(error.message || 'Failed to upgrade to subscription. Please try again.');
    } finally {
      this.setLoading(false);
      this.isInternalRequest = false;
    }
  }

  async addToCart(variantId, quantity = 1, openSidecart = true) {
    if (this.isLoading) return;

    // Validate variantId before making request
    const parsedVariantId = parseInt(variantId, 10);
    if (!variantId || isNaN(parsedVariantId)) {
      console.error('Invalid variant ID:', variantId);
      this.showError('Unable to add item. Invalid product variant.');
      return;
    }

    this.setLoading(true);
    this.isInternalRequest = true;

    try {
      const response = await this.fetchWithRetry('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parsedVariantId,
          quantity: quantity
        })
      });

      // fetchWithRetry already handles 429 retries
      // Check content type - Shopify returns text/javascript for .js endpoints
      const contentType = response.headers.get('content-type') || '';
      const isValidContentType = contentType.includes('application/json') || contentType.includes('text/javascript');
      
      if (!isValidContentType) {
        console.error('Server returned unexpected response. Status:', response.status, 'Content-Type:', contentType);
        throw new Error('Unable to add item right now. Please refresh the page and try again.');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }

      await this.refreshCart();
      this.dispatchCartUpdate();
      
      // Open sidecart after adding item
      if (openSidecart) {
        this.open();
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(error.message || 'Failed to add item. Please try again.');
    } finally {
      this.setLoading(false);
      this.isInternalRequest = false;
    }
  }

  async refreshCart() {
    try {
      // Fetch updated cart section using section rendering API
      const response = await fetch(`${window.location.pathname}?sections=${this.sectionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const data = await response.json();
      const html = data[this.sectionId];
      
      if (html) {
        this.updateSidecartContent(html);
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
      // Try alternative endpoint
      await this.refreshCartAlternative();
    }
  }

  /**
   * Alternative method to refresh cart using root endpoint
   */
  async refreshCartAlternative() {
    try {
      const response = await fetch(`/?sections=${this.sectionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch cart from alternative endpoint');
      }

      const data = await response.json();
      const html = data[this.sectionId];
      
      if (html) {
        this.updateSidecartContent(html);
      }
    } catch (error) {
      console.error('Error refreshing cart (alternative):', error);
    }
  }

  /**
   * Updates the sidecart DOM with new HTML content
   * Preserves add-ons section and reinitializes swiper properly
   */
  updateSidecartContent(html) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Get the new sidecart elements
    const newSidecart = tempDiv.querySelector('[data-sidecart]');
    const newOverlay = tempDiv.querySelector('[data-sidecart-overlay]');
    
    if (newSidecart && this.sidecart) {
      const wasOpen = this.isOpen();
      const wasLoading = this.isLoading;
      
      // Destroy existing swiper before replacing content
      if (this.swiper) {
        try {
          this.swiper.destroy(true, true);
        } catch (e) {
          console.warn('Error destroying swiper:', e);
        }
        this.swiper = null;
      }
      
      // Replace inner content only to preserve event listeners on container
      this.sidecart.innerHTML = newSidecart.innerHTML;
      
      // Preserve open state
      if (wasOpen) {
        this.sidecart.classList.add('is-active');
        this.sidecart.setAttribute('aria-hidden', 'false');
      }
      
      // Update close buttons reference
      this.closeButtons = this.sidecart.querySelectorAll('[data-sidecart-close]');
      this.closeButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          // Only prevent default if it's not a link with a valid href
          const isLinkWithHref = button.tagName === 'A' && button.hasAttribute('href') && button.getAttribute('href') !== '#';
          if (!isLinkWithHref) {
            e.preventDefault();
          }
          this.close();
          // If it's a link, let the browser handle navigation naturally
        });
      });
      
      // Update swiper references
      this.swiperContainer = this.sidecart.querySelector('[data-sidecart-addons-swiper]');
      
      // Check if add-ons section exists in the new content
      const addonsSection = this.sidecart.querySelector('[data-sidecart-addons]');
      if (addonsSection) {
        // Ensure add-ons section is visible
        addonsSection.style.display = '';
      }
      
      // Initialize swiper with proper delay to ensure DOM is ready
      if (this.swiperContainer) {
        // Use requestAnimationFrame for more reliable timing
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.initSwiper();
          });
        });
      }
      
      // Reset loading state on buttons if we were loading
      if (wasLoading) {
        this.setLoading(false);
      }
    }
  }

  dispatchCartUpdate() {
    document.dispatchEvent(new CustomEvent('cart:updated'));
    
    // Update cart count in header if exists
    this.updateCartCount();
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  setLoading(loading) {
    this.isLoading = loading;
    
    if (this.sidecart) {
      this.sidecart.classList.toggle('is-loading', loading);
      
      // Disable buttons during loading using both disabled and aria-disabled for accessibility
      const buttons = this.sidecart.querySelectorAll('button, a.c-sidecart__btn');
      buttons.forEach(btn => {
        if (loading) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
        }
      });
    }
  }

  /**
   * Force reset loading state - useful for error recovery
   */
  forceResetLoading() {
    this.isLoading = false;
    this.isInternalRequest = false;
    this.setLoading(false);
  }

  showError(message) {
    // You can customize this to show a toast notification or inline error
    console.error(message);
    alert(message);
    
    // Always ensure loading state is reset after showing error
    this.forceResetLoading();
  }
}

// Initialize sidecart when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cSidecart = new CSidecart();
});

// Also handle Shopify's section rendering
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-sidecart]')) {
      window.cSidecart = new CSidecart();
    }
  });
}
